import React from "react";

/**
 * Item de listagem do fluxo de agendamento.
 *
 * Unifica os tres cards que existiam separados: agendamento salvo, sala e
 * horario. Cada um usava uma montagem propria da mesma estrutura.
 *
 * Quando recebe `onClick`, o card inteiro vira acionavel — por mouse, teclado
 * (Enter/Espaco) e leitores de tela. Quando `disabled`, deixa de responder mas
 * continua legivel, sinalizado por aria-disabled.
 *
 * @param {string}   [leadingIcon]   classe do icone a esquerda
 * @param {string}   title           texto principal
 * @param {string}   [subtitle]      texto secundario
 * @param {string}   [trailingIcon]  classe do icone que indica a acao do card
 * @param {() => void} [onClick]     torna o card inteiro acionavel
 * @param {boolean}  [disabled]
 * @param {{icon: string, label: string, onClick: () => void}} [action]
 *        acao secundaria propria, como excluir
 */
export default function CardItem({
  leadingIcon,
  title,
  subtitle,
  trailingIcon,
  onClick,
  disabled = false,
  action,
}) {
  const isClickable = Boolean(onClick) && !disabled;

  function handleKeyDown(e) {
    if (!isClickable) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick();
    }
  }

  return (
    <div
      className={
        "sched-item" +
        (isClickable ? " sched-item-clickable" : "") +
        (disabled ? " sched-item-disabled" : "")
      }
      onClick={isClickable ? onClick : undefined}
      onKeyDown={handleKeyDown}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      aria-disabled={disabled ? "true" : undefined}
    >
      {leadingIcon && (
        <span className="sched-item-icon" aria-hidden="true">
          <i className={leadingIcon} />
        </span>
      )}

      <div className="sched-item-body">
        <span className="sched-item-title">{title}</span>
        {subtitle && <span className="sched-item-subtitle">{subtitle}</span>}
      </div>

      {trailingIcon && !disabled && (
        <span className="sched-item-trailing" aria-hidden="true">
          <i className={trailingIcon} />
        </span>
      )}

      {action && (
        <button
          type="button"
          className="sched-item-action"
          aria-label={action.label}
          onClick={(e) => {
            // impede que o clique acione tambem o card, quando ele e acionavel
            e.stopPropagation();
            action.onClick();
          }}
        >
          <i className={action.icon} aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
